import { Directive, ElementRef, HostListener, Input, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

export type TextCaseMode = 'none' | 'uppercase' | 'lowercase' | 'titlecase' | 'capitalize';

@Directive({
  selector: '[appTextCase]',
  standalone: true,
})
export class TextCaseReusableDirective {
  @Input('appTextCase') mode: TextCaseMode = 'none';
  private isComposing = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    @Optional() private readonly ngControl?: NgControl,
  ) {}

  @HostListener('compositionstart')
  onCompositionStart(): void {
    this.isComposing = true;
  }

  @HostListener('compositionend')
  onCompositionEnd(): void {
    this.isComposing = false;
    this.applyTransform(false);
  }

  @HostListener('input')
  onInput(): void {
    if (this.isComposing) return;
    this.applyTransform(false);
  }

  @HostListener('blur')
  onBlur(): void {
    this.applyTransform(true);
  }

  private applyTransform(trimEdges: boolean): void {
    if (this.mode === 'none') return;

    const element = this.elementRef.nativeElement;
    if (element instanceof HTMLInputElement && this.shouldSkipInputType(element.type)) {
      return;
    }

    const currentValue = element.value ?? '';
    const normalized = this.normalize(currentValue, trimEdges);

    if (normalized === currentValue) return;

    const selectionStart = element.selectionStart;
    const selectionEnd = element.selectionEnd;

    element.value = normalized;

    if (selectionStart !== null && selectionEnd !== null) {
      const delta = normalized.length - currentValue.length;
      const nextStart = Math.max(0, selectionStart + delta);
      const nextEnd = Math.max(0, selectionEnd + delta);
      element.setSelectionRange(nextStart, nextEnd);
    }

    if (this.ngControl?.control) {
      this.ngControl.control.setValue(normalized);
      this.ngControl.control.markAsDirty();
      return;
    }
  }

  private shouldSkipInputType(type: string): boolean {
    return ['number', 'date', 'time'].includes(type);
  }

  private normalize(value: string, trimEdges: boolean): string {
    const source = trimEdges ? value.trim() : value;

    if (this.mode === 'uppercase') return source.toUpperCase();
    if (this.mode === 'lowercase') return source.toLowerCase();

    if (this.mode === 'capitalize') {
      if (!source) return source;
      return source.charAt(0).toUpperCase() + source.slice(1);
    }

    if (this.mode === 'titlecase') {
      return source
        .split(/(\s+)/)
        .map((part) => (/^\s+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
        .join('');
    }

    return source;
  }
}
